/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import React, { useState, useEffect, useRef } from 'react';
import {
  useNavigate, useParams, useSearchParams,
} from 'react-router-dom';
import i18n from 'i18next';
import { Modal } from '@myinterview/component-library';
import * as Sentry from '@sentry/react';
import { useIntercom } from 'react-use-intercom';
import { useTranslation } from 'react-i18next';
import {
  getPublicJob, getPrivateJob, sendApplicationOverQuota, updateStatus,
} from '../../services/interviewService';
import { GAevent } from '../../utils/googleAnalytics';
import {
  JobHeader,
  JobDescription,
  CandidateDetails,
  ButtonContainer,
  VideoPlayer,
  Form,
  TopTips,
  RightSide,
} from '../../components';
import { IParsedInputField, IJobResponse } from '../../interfaces/jobInterface';
import {
  IApplicationOverQuotaData, ICandidateDetails, IMessageType, IUserBasicDetails,
} from '../../interfaces';
import { PopupMessage } from '../../components/PopupMessage';
import { PopupForm } from '../../components/PopupForm';
import { useCustomization } from '../../hooks/useCustomization';
import { getCandidateSession, registerDateEvent, updateSession } from '../../services/sessionService';
import { CandidateSessionSourceMap, ICandidateSession } from '../../interfaces/candidateSessionInterface';
import { getCookie, hideIntercom } from '../../utils/utils';
import { emitTrackEvent, EVENT_TYPES, updateEventId } from '../../services/eventService';

const FORM_EVENT_DEBOUNCE_TIME = 300;

interface IInviteOrigin {
  // eslint-disable-next-line react/require-default-props
  isPrivateLink?: boolean;
}

const Main = ({ isPrivateLink = false }: IInviteOrigin) => {
  const { boot, hide } = useIntercom();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messageType, setMessageType] = useState<IMessageType | null>(null);
  const [overQuotaFormButtonText, setOverQuotaFormButtonText] = useState<string>('inactiveJob.request_interview');
  const [topTipsOpen, setTopTipsOpen] = useState<boolean>(false);
  const [jobDetails, setJobDetails] = useState<IJobResponse | null>(null);
  const [inputFieldsData, setInputFieldsData] = useState<IParsedInputField>({});
  const [candidateDetails, setCandidateDetails] = useState<ICandidateDetails>({
    firstName: '', lastName: '', email: '', isValid: false,
  });
  const [cvLinkValid, setCvLinkValid] = useState<boolean>(true);
  const [btnDisabled, setBtnDisabled] = useState<boolean>(true);
  const [stopVideo, setStopVideo] = useState<boolean>(false);
  const navigate = useNavigate();
  const {
    jobId, companySlug, jobSlug,
  } = useParams();
  const [query] = useSearchParams();
  const rid = query.get('rid') || '';
  const qid = query.get('qid') || '';
  const queryReferrer = query.get('referrer') || '';
  const candidateID = query.get('candidateID') || '';
  const eventDebouncer = useRef<NodeJS.Timeout>();
  const { t } = useTranslation();
  const { candidateId } = useParams();
  const lastFieldUpdated = useRef<string>('');

  const setInterview = async () => {
    try {
      setIsLoading(true);
      const data = isPrivateLink
        ? await getPrivateJob(candidateId, undefined, undefined, queryReferrer)
        : await getPublicJob(jobId, jobSlug, companySlug, qid, rid, undefined, undefined, queryReferrer);

      if (data.event_id) {
        updateEventId(data.event_id);
      }

      const originalEventId = getCookie(data.job.job_id);
      if (originalEventId) {
        query.set('unique_id', originalEventId);
        navigate({ search: query.toString() });
      }

      let sourceEvent: keyof typeof CandidateSessionSourceMap = 'direct_start_inter';

      if (isPrivateLink) {
        const url = window.location.search;
        if (url.includes('s=1&r=1')) {
          sourceEvent = 'reminder_start_inter';
        } else if (url.includes('s=2&r=1')) {
          sourceEvent = 'reminder_sms_start_inter';
        } else if (url.includes('s=1')) {
          sourceEvent = 'email_start_inter';
        } else if (url.includes('s=2')) {
          sourceEvent = 'sms_start_inter';
        }
      }
      // if the request didn't fail we should be able to retrieve and store the session object
      if (data.session_id) {
        const session = await getCandidateSession(undefined, data.session_id) || {} as ICandidateSession;
        session.source = CandidateSessionSourceMap[sourceEvent];
        await updateSession(session);
      }
      useCustomization({ apiKey: data.job.apiKey });
      GAevent(sourceEvent);
      setJobDetails(data);
      if (candidateId && data.candidate?.status === 'pending') {
        try {
          await updateStatus({
            candidate_id: candidateId,
            inviteId: candidateId,
            status: 'clicked',
          });
        } catch (err) {
          Sentry.captureException(err);
        }
      }
    } catch (err) {
      console.error('Error happened with setting the interview', err);
      Sentry.captureException(err);
      setJobDetails(null);
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setInterview();
  }, []);

  const handleUpdateDetails = (data: ICandidateDetails, updateFiled: string) => {
    lastFieldUpdated.current = updateFiled;
    setCandidateDetails({
      isValid: data.isValid,
      firstName: data.firstName.replace(/\r?\n|\r/, ' ').replace(/\s+/g, ' ').trim(), // Remove linebreaks and Extra spaces
      lastName: data.lastName.replace(/\r?\n|\r/, ' ').replace(/\s+/g, ' ').trim(),
      email: data.email.replace(/\r?\n|\r/, ' ').replace(/\s+/g, ' ').trim(),
    });
  };

  const triggerFormEvent = () => emitTrackEvent({ eventType: EVENT_TYPES.FORM_FILLED, extraData: { candidateDetails, inputFieldsData, currentFiled: lastFieldUpdated.current } });

  const handleInputFieldsData = (fieldData: any) => { // TODO: to check type
    lastFieldUpdated.current = fieldData.name;
    setInputFieldsData((() => ({ ...inputFieldsData, [fieldData.name]: { value: fieldData.value || '', type: fieldData.type } })));
  };

  useEffect(() => {
    if (jobDetails && !hideIntercom(jobDetails.job.company_id)) {
      const isRtl = ['he', 'ar'].includes(jobDetails.job.language);
      boot({
        customAttributes: {
          is_new_widget: true,
        },
        alignment: isRtl ? 'left' : 'right',
      });
    }

    return () => {
      hide();
    };
  }, [jobDetails]);

  useEffect(() => {
    if (inputFieldsData.cvUrl) {
      setCvLinkValid(inputFieldsData.cvUrl.value !== '');
    }
    const isAnyFieldValid = Object.values(inputFieldsData).some(({ value }) => value);
    if (isAnyFieldValid && lastFieldUpdated.current && typeof eventDebouncer.current !== 'undefined') {
      clearTimeout(eventDebouncer.current);
      eventDebouncer.current = setTimeout(() => {
        triggerFormEvent();
      }, FORM_EVENT_DEBOUNCE_TIME);
    }
  }, [inputFieldsData]);

  useEffect(() => {
    const invalid = !(candidateDetails.isValid && cvLinkValid) && !(isPrivateLink && cvLinkValid);
    setBtnDisabled(invalid);
    if (!invalid && lastFieldUpdated.current && typeof eventDebouncer.current !== 'undefined') {
      clearTimeout(eventDebouncer.current);
      eventDebouncer.current = setTimeout(() => {
        registerDateEvent('form_filled').then(() => {});
        triggerFormEvent();
      }, FORM_EVENT_DEBOUNCE_TIME);
    }
  }, [candidateDetails, cvLinkValid]);

  useEffect(() => {
    const isCandidateExpired = (jobDetails?.candidate?.deadline && Date.parse(jobDetails?.candidate?.deadline) < Date.now()) || ['deleted', 'cancelled'].includes(jobDetails?.status || '');
    const isJobExpired = !jobDetails?.candidate?.deadline && jobDetails?.job?.deadlineDate && Date.parse(jobDetails?.job?.deadlineDate) < Date.now();
    const isJobNotAccessible = ['paused', 'cancelled'].includes(jobDetails?.subscription?.status || '') || ['archived', 'draft'].includes(jobDetails?.job.status || '');
    const isJobApplied = jobDetails?.status === 'completed';
    if (isCandidateExpired || isJobExpired || isJobNotAccessible || isJobApplied) {
      const jobMessageType: IMessageType = isJobApplied ? 'applied' : (isJobExpired || isCandidateExpired) ? 'deadline' : 'inactiveJob';
      /* const toLog = `Application page not accessible:
       isCandidateExpired: ${isCandidateExpired},
       isJobExpired: ${isJobExpired},
       isJobNotAccessible: ${isJobNotAccessible},
       isJobApplied: ${isJobApplied},
       subscription: ${JSON.stringify(jobDetails?.subscription || {})}
       jobDetails: ${JSON.stringify(jobDetails?.job || {})}`;
      console.log(toLog); */
      setMessageType(jobMessageType);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    const defaultInputFieldsData: IParsedInputField = {};
    if (jobDetails?.job.jobInputFields) {
      jobDetails.job.jobInputFields
        .forEach((inputField) => {
          defaultInputFieldsData[inputField.fieldName] = { value: '', type: inputField.type };
        });
    }
    if (jobDetails?.video?.files?.length) {
      jobDetails.video.files.forEach((v) => {
        const match = v.match(/--(.+)--(.+)/);
        if (match && match[1] && match[2]) {
          const [_, fieldName] = v.split('--');
          defaultInputFieldsData[fieldName] = { value: v, type: 'document' };
        }
      });
    }
    setInputFieldsData(defaultInputFieldsData);

    if (jobDetails) {
      i18n.changeLanguage(jobDetails.job.language);
      ['he', 'ar'].includes(jobDetails.job.language) && document.documentElement.setAttribute('data-direction', 'rtl');
    }
    if (jobDetails?.quizDetails) {
      setCandidateDetails({ ...jobDetails?.quizDetails, isValid: true });
    }
  }, [jobDetails]);

  const onOpenModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    GAevent('tips_open');
    emitTrackEvent({ eventType: EVENT_TYPES.TOP_TIPS_CLICKED });
    registerDateEvent('top_tips_shown')
      .then(() => {

      });
    setTopTipsOpen(true);
  };

  const handleWidgetEvent = (type: IMessageType = 'completed'):void => {
    setMessageType(type);
  };

  const onSendApplicationOverQuota = (userDetails:IUserBasicDetails) => {
    if (jobDetails?.job) {
      const { creatorId, logo, title } = jobDetails.job;
      const data:IApplicationOverQuotaData = {
        ...userDetails,
        creatorId,
        logo,
        title,
      };
      setOverQuotaFormButtonText('inactiveJob.sending');
      sendApplicationOverQuota(data)
        .then(() => setOverQuotaFormButtonText('inactiveJob.thanks'))
        .catch(() => setOverQuotaFormButtonText('inactiveJob.try_again'));
    }
  };

  const videoComponent = () => {
    if (jobDetails?.job.introVideo) {
      return (
        <div className="video-container">
          <VideoPlayer shouldStopVideo={stopVideo} videoUrl={jobDetails?.job.introVideo} />
        </div>
      );
    }
    return <div className="no-video-spacer" />;
  };

  return (
    <div className="main">
      <div className="left-side">
        <div className="main__wrapper">
          <JobHeader
            companyName={jobDetails?.job?.company || ''}
            /* TODO: check if we can remove the logo from the response
             (backend should return it inside the job object)
            logoUrl={jobDetails?.job?.url || jobDetails?.logo} */
            logoUrl={jobDetails?.job?.logo || 'https://logo.clearbit.com/myinterview.com'}
            jobTitle={jobDetails?.job?.title || ''}
            isLoading={isLoading}
          />
          {!messageType && (
          <>
            {videoComponent()}
            <JobDescription
              isLoading={isLoading}
              candidateName={jobDetails?.candidate?.username || jobDetails?.quizDetails?.firstName || ''}
              jobDetails={jobDetails?.job}
            />
            {(!isPrivateLink && !jobDetails?.quizDetails)
              && (
                <CandidateDetails
                  isLoading={isLoading}
                  onInputDetails={handleUpdateDetails}
                  companyId={jobDetails?.job.company_id}
                />
              )}
            {!!(jobDetails && jobDetails.job.jobInputFields?.length)
              && (
                <Form
                  inputFieldsData={inputFieldsData}
                  jobInputFields={jobDetails.job.jobInputFields}
                  handleInputField={handleInputFieldsData}
                />
              )}
            {jobDetails
              && (
              <ButtonContainer
                btnDisabled={btnDisabled}
                job={jobDetails}
                candidate={candidateDetails}
                video={jobDetails.video}
                candidateID={candidateID}
                inputFieldsData={inputFieldsData}
                status={jobDetails.status}
                handleTopTipsClicked={onOpenModal}
                handleWidgetEvent={handleWidgetEvent}
                handleStopVideo={() => setStopVideo(true)}
              />
              )}
          </>
          )}
        </div>
      </div>
      <RightSide
        isLoading={isLoading}
        backgroundColor={jobDetails?.job.overlay || ''}
        backgroundImage={jobDetails?.job.backgroundImage || ''}
        showWatermark={(!isLoading && jobDetails?.showWatermark) ?? true}
      />
      <Modal
        closeButton
        clickOutside
        backgroundCover={topTipsOpen}
        className={`toptips__modal dynamic-direction ${topTipsOpen ? 'toptips__modal--opened' : ''}`}
        onClose={() => setTopTipsOpen(false)}
      >
        <TopTips />
      </Modal>
      {messageType && (
      <Modal fullScreen={window.innerWidth < 767} className="overlay-modal" onClose={() => {}} backgroundCover>
        {messageType !== 'inactiveJob'
          ? (
            <PopupMessage
              messageType={messageType}
              outroVideo={jobDetails?.job.outroVideo}
              thankYouMessage={jobDetails?.job.thankYouMsg}
              companyName={jobDetails?.job.company || ''}
              candidateEmail={jobDetails?.candidate?.email || candidateDetails.email}
            />
          )
          : (
            <PopupForm
              overQuotaFormButtonText={t(overQuotaFormButtonText)}
              onSendApplicationOverQuota={onSendApplicationOverQuota}
            />
          ) }
      </Modal>
      )}
    </div>
  );
};

export default Main;
